// src/pages/About.js
import { Link } from "react-router-dom";
import { ArrowLeft, Users, Zap, Globe, Shield, Target, Heart } from "lucide-react";

function About() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center text-slate-300 hover:text-white mb-8 transition"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            About SkillSyncs
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl">
            The '911' for tech issues. We connect businesses and individuals with expert technical helpers to solve problems fast, anywhere in the world.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Our Mission */}
        <section className="mb-16">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 md:p-12">
            <div className="flex items-center mb-6">
              <Target className="w-8 h-8 text-blue-600 mr-3" />
              <h2 className="text-3xl font-bold text-slate-900">Our Mission</h2>
            </div>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              At SkillSyncs, we believe that technical problems shouldn't slow you down. Our mission is to democratize access to expert technical assistance by creating a global platform that connects those who need help with those who can provide it—instantly, efficiently, and reliably.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed">
              We're building a world where every technical challenge has a solution, where expertise is accessible regardless of location, and where problem-solving happens at the speed of connection.
            </p>
          </div>
        </section>

        {/* What We Do */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">What We Do</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
              <div className="flex items-center mb-4">
                <Zap className="w-6 h-6 text-blue-600 mr-3" />
                <h3 className="text-xl font-semibold text-slate-900">Intelligent Matching</h3>
              </div>
              <p className="text-slate-700 leading-relaxed">
                Our advanced matching algorithm connects clients with the most qualified helpers based on skills, expertise, availability, and problem complexity. We ensure the right expert is matched with the right problem every time.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
              <div className="flex items-center mb-4">
                <Globe className="w-6 h-6 text-blue-600 mr-3" />
                <h3 className="text-xl font-semibold text-slate-900">Global Reach</h3>
              </div>
              <p className="text-slate-700 leading-relaxed">
                With helpers and clients spanning continents, we provide 24/7 support across time zones. Whether you're in New York or Tokyo, help is always available when you need it.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
              <div className="flex items-center mb-4">
                <Shield className="w-6 h-6 text-blue-600 mr-3" />
                <h3 className="text-xl font-semibold text-slate-900">Secure Workspace</h3>
              </div>
              <p className="text-slate-700 leading-relaxed">
                Our integrated workspace provides a secure environment for collaboration, with real-time messaging, file sharing, progress tracking, and intelligent features that make problem-solving efficient and transparent.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
              <div className="flex items-center mb-4">
                <Users className="w-6 h-6 text-blue-600 mr-3" />
                <h3 className="text-xl font-semibold text-slate-900">Expert Network</h3>
              </div>
              <p className="text-slate-700 leading-relaxed">
                We've built a curated network of verified technical experts across diverse domains—from software development and AI/ML engineering to data science and cloud infrastructure. Quality is our priority.
              </p>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="mb-16">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 md:p-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Our Story</h2>
            <div className="space-y-4 text-slate-700 leading-relaxed">
              <p className="text-lg">
                SkillSyncs was born from a simple observation: technical problems are universal, but access to expert solutions isn't. Too often, businesses and individuals face critical technical challenges without immediate access to the right expertise.
              </p>
              <p>
                We recognized that the world is full of talented technical professionals ready to help, but the traditional barriers—geography, time zones, and inefficient matching—prevented these connections from happening quickly enough.
              </p>
              <p>
                Today, SkillSyncs serves as a bridge, connecting clients with urgent technical needs to qualified helpers who can provide solutions. Our platform leverages intelligent matching, secure collaboration tools, and a global network of experts to ensure that help arrives when it matters most.
              </p>
              <p>
                We're more than a platform—we're a movement toward making technical expertise accessible, affordable, and available to everyone, everywhere.
              </p>
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Values</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Speed</h3>
              <p className="text-slate-700">
                We understand that time is critical. Our platform is designed for rapid problem resolution, from intelligent matching to real-time collaboration.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Trust</h3>
              <p className="text-slate-700">
                Security and reliability are foundational. We verify our helpers, protect your data, and ensure every interaction is secure and professional.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Excellence</h3>
              <p className="text-slate-700">
                We're committed to quality in everything we do—from the experts we onboard to the solutions we facilitate. Excellence is not optional; it's our standard.
              </p>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="mb-16">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 md:p-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">How SkillSyncs Works</h2>
            <div className="space-y-8">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Submit Your Problem</h3>
                  <p className="text-slate-700 leading-relaxed">
                    Clients describe their technical issue, provide relevant details, and specify urgency. Our intelligent system analyzes the problem and identifies the best-matched helpers.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Get Matched</h3>
                  <p className="text-slate-700 leading-relaxed">
                    Our algorithm matches you with qualified helpers based on skills, experience, availability, and problem complexity. Helpers can review and accept problems that match their expertise.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Collaborate Securely</h3>
                  <p className="text-slate-700 leading-relaxed">
                    Once matched, Customers and Experts work together in our secure workspace. Real-time messaging, file sharing, progress tracking, and intelligent features facilitate efficient problem-solving.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                  4
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Solve & Complete</h3>
                  <p className="text-slate-700 leading-relaxed">
                    Problems are resolved through collaborative effort. Both parties mark completion, and the solution is documented. Our platform tracks history and builds reputation for future matches.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose SkillSyncs */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Why Choose SkillSyncs?</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">For Clients</h3>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Fast access to verified technical experts</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Intelligent matching ensures the right expert</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Secure workspace for confidential collaboration</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>24/7 global support across time zones</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Transparent progress tracking and communication</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">For Helpers</h3>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Access to diverse technical challenges</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Build reputation and professional history</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Work on problems that match your expertise</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Flexible availability and remote collaboration</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Professional platform with secure tools</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Global Impact */}
        <section className="mb-16">
          <div className="bg-slate-900 text-white rounded-lg p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Our Global Impact</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">Global</div>
                <p className="text-blue-100">Serving requesters and specialists across continents</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">24/7</div>
                <p className="text-blue-100">Round-the-clock support availability</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">Verified</div>
                <p className="text-blue-100">Curated network of specialist professionals</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 md:p-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Join the SkillSyncs Community</h2>
            <p className="text-lg text-slate-700 mb-8 max-w-2xl mx-auto">
              Whether you need technical help or want to share your expertise, SkillSyncs is the platform for you. Join thousands of requesters and specialists solving problems together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/submit-problem"
                className="inline-flex items-center justify-center px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition"
              >
                Get Started as Customer
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center justify-center px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition"
              >
                Become an Expert
              </Link>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="mt-16 text-center">
          <div className="bg-slate-100 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Get in Touch</h2>
            <p className="text-slate-700 mb-4">
              Have questions? We'd love to hear from you.
            </p>
            <a
              href="mailto:skillsyncs@outlook.com"
              className="text-blue-600 hover:text-blue-700 font-semibold"
            >
              skillsyncs@outlook.com
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}

export default About;

